/**
 * 新建图元 op（矩形/椭圆/直线/折线/文本）。
 *
 * 与其它结构 op 同一套路：**构造阶段零副作用** —— 元素先在内存里建好并返回，
 * 由交互层插进 DOM 做预览，松手时再把它包成一条命令（apply 只在撤销后重做时才动手）。
 * id 一律 `ext-N`（与参考产品一致，且不与原文档 id 冲突）。
 */
import { SVG_NS } from '../dom';
import type { Pt, Rect } from '../geom/matrix';
import type { Command } from '../history';
import { nextExtId } from '../ids';
import { makeStructuralPatch, snapshotOf } from '../patch';

export type ShapeTag = 'rect' | 'ellipse' | 'line' | 'polyline' | 'text';

export interface ShapeSpec {
  tag: ShapeTag;
  /** 几何（根用户坐标下拖出的框） */
  box: Rect;
  /** 虚线/描边等额外属性 */
  attrs?: Record<string, string>;
}

/** 新元素的默认样式（编辑器当前描边色，避免"看不见的新元素"） */
export const DEFAULT_STROKE = '#1b1f24';
export const DEFAULT_STROKE_WIDTH = '1';
export const MIN_SIZE = 1;

const round = (n: number): number => Math.round(n * 100) / 100;

/** 由拖出的矩形换算成各图元的几何属性 */
export function attrsForShape(spec: ShapeSpec): Record<string, string> {
  const { x, y, w, h } = spec.box;
  const left = round(Math.min(x, x + w));
  const top = round(Math.min(y, y + h));
  const width = round(Math.abs(w));
  const height = round(Math.abs(h));
  switch (spec.tag) {
    case 'rect':
      return { x: String(left), y: String(top), width: String(width), height: String(height) };
    case 'ellipse':
      return {
        cx: String(round(left + width / 2)),
        cy: String(round(top + height / 2)),
        rx: String(round(width / 2)),
        ry: String(round(height / 2)),
      };
    case 'line':
      return {
        x1: String(round(x)),
        y1: String(round(y)),
        x2: String(round(x + w)),
        y2: String(round(y + h)),
      };
    case 'polyline':
      return { points: `${round(x)},${round(y)} ${round(x + w)},${round(y + h)}` };
    case 'text':
      return { x: String(round(x)), y: String(round(y + height || y)) };
  }
}

/** 在内存里建元素（未入树；由调用方插入或丢弃） */
export function buildShape(root: Element, spec: ShapeSpec): Element {
  const doc = root.ownerDocument;
  const el = doc.createElementNS(SVG_NS, spec.tag);
  el.setAttribute('id', nextExtId(root));
  const attrs: Record<string, string> = { ...attrsForShape(spec), ...(spec.attrs ?? {}) };
  for (const [name, value] of Object.entries(attrs)) el.setAttribute(name, value);

  if (spec.tag === 'text') {
    const size = Number(attrs['font-size'] ?? 16);
    el.setAttribute('font-size', String(Number.isFinite(size) ? size : 16));
    el.setAttribute('fill', attrs['fill'] ?? DEFAULT_STROKE);
    el.textContent = '文字';
    return el;
  }
  if (spec.tag === 'rect' || spec.tag === 'ellipse') {
    el.setAttribute('fill', attrs['fill'] ?? 'none');
  }
  el.setAttribute('stroke', attrs['stroke'] ?? DEFAULT_STROKE);
  el.setAttribute('stroke-width', attrs['stroke-width'] ?? DEFAULT_STROKE_WIDTH);
  if (spec.tag === 'polyline') el.setAttribute('fill', attrs['fill'] ?? 'none');
  return el;
}

/** 图形太小视为误点（不产生元素） */
export const isDegenerate = (spec: ShapeSpec): boolean =>
  spec.tag !== 'text' &&
  Math.abs(spec.box.w) < MIN_SIZE &&
  Math.abs(spec.box.h) < MIN_SIZE;

/**
 * 把**已经插入 DOM** 的预览元素包成一条命令。
 * apply 只在撤销之后重做时才需要真正动手（预览期间它已在树里）。
 */
export function commitShapeCommand(parent: Element, el: Element, before: string, label: string): Command {
  return {
    label,
    patches: [
      makeStructuralPatch(parent, before, label, () => {
        if (el.parentElement !== parent) parent.appendChild(el);
      }),
    ],
  };
}

/** 无预览路径（例如"复制一份"）：直接一条命令插入 */
export function insertShape(root: Element, parent: Element, spec: ShapeSpec): { cmd: Command; element: Element } {
  const el = buildShape(root, spec);
  const before = snapshotOf(parent);
  return { cmd: commitShapeCommand(parent, el, before, `新建 ${spec.tag}`), element: el };
}

/** 折线：由点列表生成 points 字符串 */
export const pointsAttr = (pts: readonly Pt[]): string =>
  pts.map((p) => `${round(p.x)},${round(p.y)}`).join(' ');
